/**
 * /settings → Workpackages → Branches (admin-only).
 *
 * Beheer-UI voor branches binnen één workpackage: lijst, aanmaken
 * (vanaf andere branch óf vanaf snapshot), hernoemen, status wijzigen
 * (active / merged / abandoned), verwijderen.
 *
 * Indeling vergelijkbaar met de Folder-permissies-subtab: eerst een WP
 * kiezen, daarna de branches van die WP. Source-kolom toont de
 * parent-branch — voor non-main-branches is dat normaliter de main van
 * dezelfde WP.
 *
 * Niet bedoeld voor de **dagelijkse** branch-switch (die zit in de
 * branch-keuzelijst van de Explorer).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  type BranchSummary,
  type SnapshotMetadata,
  type WorkPackageDTO,
} from "../api/client";
import { AiBadge } from "../editor/AiBadge";
import { useSession } from "../state/session";
import { useWorkspaceData } from "../state/workspace";
import { MergeBranchDialog } from "./MergeBranchDialog";

interface Props {
  workspaceId: string;
  /** Wanneer aanwezig: WP-picker bovenaan i.p.v. workspace-wide-list.
   *  Mounted vanuit WorkpackagesSettings → subtab Branches. */
  workpackages?: WorkPackageDTO[];
}

export function BranchesSettings({ workspaceId, workpackages }: Props) {
  const { t } = useTranslation();
  const currentBranchId = useSession((s) => s.branchId);
  const sessionWpId = useSession((s) => s.workpackageId);

  // WP-scope: alleen open WPs in de picker. Default = huidige session-WP.
  const selectableWps = useMemo(
    () => (workpackages ?? []).filter((wp) => wp.status === "open"),
    [workpackages],
  );
  const [selectedWpId, setSelectedWpId] = useState<string | null>(null);
  useEffect(() => {
    if (selectedWpId) return;
    if (sessionWpId && selectableWps.some((wp) => wp.id === sessionWpId)) {
      setSelectedWpId(sessionWpId);
      return;
    }
    if (selectableWps[0]) {
      setSelectedWpId(selectableWps[0].id);
    }
  }, [selectableWps, selectedWpId, sessionWpId]);

  const [branches, setBranches] = useState<BranchSummary[]>([]);
  const [snapshots, setSnapshots] = useState<SnapshotMetadata[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [mergeBranch, setMergeBranch] = useState<BranchSummary | null>(null);

  // Form-state voor "nieuwe branch".
  const [draftName, setDraftName] = useState("");
  const [draftFromBranch, setDraftFromBranch] = useState<string>("");
  const [draftFromSnapshot, setDraftFromSnapshot] = useState<string>("");

  const refresh = useCallback(async () => {
    setError(null);
    try {
      // WP-scoped pad: list branches voor de gekozen WP. Snapshots laden
      // we vanaf de main van die WP zodat ze WP-relevant zijn.
      if (selectedWpId) {
        const wpRes = await api.listWorkpackageBranches(workspaceId, selectedWpId);
        setBranches(wpRes.branches);
        const mainOfWp = wpRes.branches.find((b) => b.is_main);
        if (mainOfWp) {
          try {
            const snaps = await api.listSnapshots(workspaceId, mainOfWp.id, false);
            setSnapshots(snaps.items);
          } catch {
            setSnapshots([]);
          }
        } else {
          setSnapshots([]);
        }
        return;
      }
      // Geen WP geselecteerd. Zolang er WÉL selecteerbare WPs zijn, is dit
      // de transient bij eerste mount vóórdat het default-WP-effect
      // selectedWpId zet — dan NIET de workspace-brede lijst tonen. Die
      // mengt de main-branches van álle workpackages, en omdat de
      // "From existing branch"-dropdown uit `branches` wordt gevuld kon je
      // zo per ongeluk een cross-WP main als source kiezen → een branch die
      // onder het verkeerde WP belandt en in dit WP nergens verschijnt.
      // We wachten op het default-effect (re-trigger via selectedWpId-dep).
      if (selectableWps.length > 0) {
        setBranches([]);
        setSnapshots([]);
        return;
      }
      // Echt geen WP-context (geen enkele open WP): legacy workspace-wide-list.
      if (!currentBranchId) return;
      const [allBranches, snaps] = await Promise.all([
        api.listBranches(workspaceId),
        api.listSnapshots(workspaceId, currentBranchId, true),
      ]);
      setBranches(allBranches);
      setSnapshots(snaps.items);
    } catch (err) {
      setError(explain(err));
    }
  }, [workspaceId, currentBranchId, selectedWpId, selectableWps]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Default-bron-branch zodra branches binnen zijn — main van de gekozen WP.
  const mainBranchId = useMemo(
    () => branches.find((b) => b.is_main)?.id ?? branches[0]?.id ?? "",
    [branches],
  );

  useEffect(() => {
    if (!draftFromBranch && mainBranchId) {
      setDraftFromBranch(mainBranchId);
    }
  }, [mainBranchId, draftFromBranch]);

  // Wisselen van WP: reset form-state zodat de from-branch opnieuw
  // defaultt op de main van de nieuwe WP.
  useEffect(() => {
    setDraftFromBranch("");
    setDraftFromSnapshot("");
  }, [selectedWpId]);

  const createBranch = async () => {
    const name = draftName.trim();
    if (!name) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      let created: { name: string };
      if (draftFromSnapshot) {
        // Snapshot-bron: workspace-level endpoint. De snapshots in deze
        // lijst horen bij de geselecteerde WP (refresh laadt ze vanaf z'n
        // main), dus de nieuwe branch erft het juiste workpackage_id via
        // de snapshot's branch.
        created = await api.createBranch(workspaceId, {
          name,
          from_snapshot_id: draftFromSnapshot,
        });
      } else if (selectedWpId) {
        // From-branch (of WP-default): WP-scoped endpoint zodat de nieuwe
        // branch GEGARANDEERD onder de geselecteerde WP landt. Het
        // workspace-level pad raadde het WP uit de parent-branch en viel bij
        // een lege bron terug op de Base-WP main → branch belandde dan altijd
        // onder Base.
        created = await api.createWorkpackageBranch(workspaceId, selectedWpId, {
          name,
          ...(draftFromBranch ? { parentBranchId: draftFromBranch } : {}),
        });
      } else {
        // Geen WP-context (geen enkele open WP): legacy workspace-level.
        created = await api.createBranch(workspaceId, {
          name,
          ...(draftFromBranch ? { from_branch_id: draftFromBranch } : {}),
        });
      }
      setInfo(t("settings.branches.createdToast", { name: created.name }));
      setDraftName("");
      setDraftFromSnapshot("");
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const renameBranch = async (b: BranchSummary) => {
    const newName = window.prompt(t("settings.branches.renamePrompt", { name: b.name }), b.name);
    if (!newName || newName === b.name) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await api.patchBranch(workspaceId, b.id, { name: newName });
      setInfo(t("settings.branches.renamedToast", { name: newName }));
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (b: BranchSummary, status: BranchSummary["status"]) => {
    if (b.status === status) return;
    const confirmKey =
      status === "abandoned"
        ? "settings.branches.statusConfirmAbandoned"
        : status === "merged"
          ? "settings.branches.statusConfirmMerged"
          : "settings.branches.statusConfirmActive";
    if (!confirm(t(confirmKey, { name: b.name, status }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await api.patchBranch(workspaceId, b.id, { status });
      setInfo(t("settings.branches.statusToast", { name: b.name, status }));
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteBranch = async (b: BranchSummary) => {
    // De server verwijdert de hele deelboom; eerst vragen welke branches dat zijn.
    let subtree: Array<{ id: string; name: string; concepts: number }> = [];
    try {
      subtree = (await api.previewDeleteBranch(workspaceId, b.id)).branches;
    } catch (err) {
      setError(explain(err));
      return;
    }
    const others = subtree.filter((x) => x.id !== b.id);
    // Met plateaus eronder is samenvoegen vaak wat bedoeld wordt: dat behoudt
    // ze (branch-merge.md §3.11).
    const question =
      others.length === 0
        ? t("settings.branches.deleteConfirm", { name: b.name })
        : t("settings.branches.deleteConfirmSubtree", {
            name: b.name,
            list: others.map((x) => `• ${x.name}`).join("\n"),
          }) + (b.parent_branch_id ? t("settings.branches.deleteMergeHint") : "");
    if (!confirm(question)) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      // Als de gebruiker op de te-verwijderen branch zit, eerst session
      // verleggen naar main — anders draaien er na de delete nog
      // vervolg-API-calls (snapshots, listConcepts, hydrate) met de
      // zojuist-verwijderde branchId, met "Branch X does not exist"-
      // errors als gevolg. Refresh laadt branches daarna opnieuw zodat
      // de UI in een schone state komt.
      if (subtree.some((x) => x.id === currentBranchId) || b.id === currentBranchId) {
        const main = branches.find((br) => br.is_main);
        if (main) {
          useSession.getState().setBranchId(main.id);
        }
      }
      await api.deleteBranch(workspaceId, b.id);
      setInfo(t("settings.branches.deletedToast", { name: b.name }));
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  /** Na het samenvoegen: wie op de samengevoegde branch stond, gaat naar de
   *  ouder; Ctrl+Z van vóór de merge zou op de oude toestand afspelen. */
  const afterMerge = async (res: {
    source: { id: string; name: string };
    target: { id: string; name: string };
  }) => {
    setMergeBranch(null);
    if (currentBranchId === res.source.id) useSession.getState().setBranchId(res.target.id);
    useWorkspaceData.setState({ userUndoStack: [], userRedoStack: [] });
    const active = useSession.getState().branchId;
    if (active) await useWorkspaceData.getState().hydrate(workspaceId, active);
    setInfo(t("merge.done", { source: res.source.name, target: res.target.name }));
    await refresh();
  };

  const wpMode = !!workpackages;

  return (
    <section className="flex flex-col gap-4">
      {mergeBranch && (
        <MergeBranchDialog
          workspaceId={workspaceId}
          branch={{ id: mergeBranch.id, name: mergeBranch.name }}
          onClose={() => setMergeBranch(null)}
          onMerged={(res) => void afterMerge(res)}
        />
      )}
      {wpMode && (
        <section className="rounded border border-(--ld-border) bg-(--ld-surface) px-4 py-3">
          <h3 className="mb-2 font-semibold text-(--ld-text) text-sm">
            {t("settings.branches.wpPickerTitle")}
          </h3>
          {selectableWps.length === 0 ? (
            <p className="text-(--ld-muted) text-sm italic">
              {t("settings.branches.wpPickerEmpty")}
            </p>
          ) : (
            <select
              value={selectedWpId ?? ""}
              onChange={(e) => setSelectedWpId(e.target.value || null)}
              className="rounded border border-(--ld-control) px-3 py-1.5 text-sm focus:border-(--ld-accent) focus:outline-none"
              disabled={busy}
            >
              {selectableWps.map((wp) => (
                <option key={wp.id} value={wp.id}>
                  {wp.name}
                  {wp.isBase ? ` (${t("settings.branches.wpPickerBaseMarker")})` : ""}
                </option>
              ))}
            </select>
          )}
        </section>
      )}

      <div className="rounded border border-(--ld-border) bg-(--ld-raised) px-3 py-2 text-(--ld-text) text-sm">
        <p className="mb-1">
          <strong>{t("settings.branches.introBoldTitle")}</strong>
          {t("settings.branches.introAfter")}
        </p>
        <p className="text-(--ld-muted) text-xs">
          {t("settings.branches.introStatuses1")}
          <strong>{t("settings.branches.statusActive")}</strong>
          {t("settings.branches.statusActiveLong")}
          <strong>{t("settings.branches.statusMerged")}</strong>
          {t("settings.branches.statusMergedLong")}
          <strong>{t("settings.branches.statusAbandoned")}</strong>
          {t("settings.branches.statusAbandonedLong")}
        </p>
      </div>

      <section className="rounded border border-(--ld-border) bg-(--ld-surface) px-4 py-3">
        <h3 className="mb-2 font-semibold text-(--ld-text) text-sm">
          {t("settings.branches.newSectionTitle")}
        </h3>
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-(--ld-muted)">{t("settings.branches.nameLabel")}</span>
            <input
              type="text"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder={t("settings.branches.namePlaceholder")}
              className="rounded border border-(--ld-control) px-3 py-1.5 text-sm focus:border-(--ld-accent) focus:outline-none"
              disabled={busy}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-(--ld-muted)">{t("settings.branches.sourceLabel")}</span>
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="radio"
                  checked={!draftFromSnapshot}
                  onChange={() => setDraftFromSnapshot("")}
                  disabled={busy}
                />
                {t("settings.branches.sourceFromBranch")}
                <select
                  value={draftFromBranch}
                  onChange={(e) => setDraftFromBranch(e.target.value)}
                  disabled={busy || !!draftFromSnapshot}
                  className="rounded border border-(--ld-control) px-2 py-0.5 text-xs"
                >
                  {branches
                    .filter((b) => b.status === "active")
                    .map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="radio"
                  checked={!!draftFromSnapshot}
                  onChange={() => setDraftFromSnapshot(snapshots[0]?.id ?? "")}
                  disabled={busy || snapshots.length === 0}
                />
                {t("settings.branches.sourceFromSnapshot")}
                <select
                  value={draftFromSnapshot}
                  onChange={(e) => setDraftFromSnapshot(e.target.value)}
                  disabled={busy || !draftFromSnapshot}
                  className="rounded border border-(--ld-control) px-2 py-0.5 text-xs"
                >
                  {snapshots.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label} ({formatTimestamp(s.created_at)})
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </label>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void createBranch()}
              disabled={busy || !draftName.trim()}
              className="rounded bg-(--ld-primary) px-3 py-1.5 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
            >
              {t("settings.branches.createButton")}
            </button>
          </div>
        </div>
      </section>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={busy}
          className="rounded border border-(--ld-control) px-3 py-1.5 text-(--ld-text) text-sm hover:bg-(--ld-raised)"
        >
          {t("settings.common.refresh")}
        </button>
      </div>

      {error && (
        <p className="rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
          {error}
        </p>
      )}
      {info && (
        <p className="rounded border border-transparent bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm">
          {info}
        </p>
      )}

      {branches.length === 0 ? (
        <p className="rounded border border-dashed border-(--ld-control) bg-(--ld-surface) px-3 py-6 text-center text-(--ld-muted) text-sm italic">
          {t("settings.branches.empty")}
        </p>
      ) : (
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-(--ld-border) border-b text-left text-(--ld-muted)">
              <th className="py-1.5 pr-3">{t("settings.branches.colName")}</th>
              <th className="py-1.5 pr-3">{t("settings.branches.colStatus")}</th>
              <th className="py-1.5 pr-3">{t("settings.branches.colSource")}</th>
              <th className="py-1.5 pr-3">{t("settings.branches.colCreated")}</th>
              <th className="py-1.5 pr-3">{t("settings.branches.colCreatedBy")}</th>
              <th className="py-1.5 pr-3">{t("settings.branches.colActions")}</th>
            </tr>
          </thead>
          <tbody>
            {branches.map((b) => (
              <tr key={b.id} className="border-(--ld-rule) border-b align-top">
                <td className="py-1.5 pr-3">
                  <span className="font-medium text-(--ld-text)">{b.name}</span>
                  <span className="ml-2">
                    <AiBadge client={b.created_via_client} />
                  </span>
                  {b.id === currentBranchId && (
                    <span className="ml-2 rounded bg-(--ld-active) px-1.5 py-0.5 text-(--ld-accent) text-xs">
                      {t("settings.branches.currentBadge")}
                    </span>
                  )}
                </td>
                <td className="py-1.5 pr-3">
                  <StatusBadge status={b.status} />
                </td>
                <td className="py-1.5 pr-3 text-(--ld-muted) text-xs">
                  {b.parent_branch_id
                    ? (branches.find((p) => p.id === b.parent_branch_id)?.name ?? "—")
                    : "—"}
                  {b.fork_point_snapshot_id && (
                    <span className="ml-1 text-(--ld-muted)">
                      {t("settings.branches.sourceSnapshotMarker")}
                    </span>
                  )}
                </td>
                <td className="py-1.5 pr-3 text-(--ld-muted) text-xs tabular-nums">
                  {formatTimestamp(b.created_at)}
                </td>
                <td className="py-1.5 pr-3 text-(--ld-muted) text-xs tabular-nums">
                  {b.created_by}
                </td>
                <td className="py-1.5 pr-3">
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => void renameBranch(b)}
                      disabled={busy || b.is_main}
                      className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-text) text-xs hover:bg-(--ld-raised) disabled:opacity-50"
                      title={
                        b.is_main
                          ? t("settings.branches.renameTitleMain")
                          : t("settings.branches.renameTitleEnabled")
                      }
                    >
                      {t("settings.branches.renameButton")}
                    </button>
                    {b.status === "active" && !b.is_main && (
                      <>
                        {b.parent_branch_id && (
                          <button
                            type="button"
                            onClick={() => setMergeBranch(b)}
                            disabled={busy}
                            title={t("settings.branches.mergeButtonTitle")}
                            className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-text) text-xs hover:bg-(--ld-raised)"
                          >
                            {t("settings.branches.mergeButton", {
                              parent:
                                branches.find((p) => p.id === b.parent_branch_id)?.name ?? "—",
                            })}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void setStatus(b, "abandoned")}
                          disabled={busy}
                          className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-text) text-xs hover:bg-(--ld-raised)"
                        >
                          {t("settings.branches.abandonButton")}
                        </button>
                      </>
                    )}
                    {b.status === "abandoned" && !b.is_main && (
                      <button
                        type="button"
                        onClick={() => void setStatus(b, "active")}
                        disabled={busy}
                        className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-text) text-xs hover:bg-(--ld-raised)"
                      >
                        {t("settings.branches.reactivateButton")}
                      </button>
                    )}
                    {!b.is_main && (
                      <button
                        type="button"
                        onClick={() => void deleteBranch(b)}
                        disabled={busy}
                        className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 font-medium text-(--ld-negative) text-xs hover:bg-(--ld-negative-bg) disabled:opacity-50"
                        title={t("settings.branches.deleteButtonTitle")}
                      >
                        {t("settings.branches.deleteButton")}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function StatusBadge({ status }: { status: BranchSummary["status"] }) {
  const { t } = useTranslation();
  const config = {
    active: {
      label: t("settings.branches.badgeActive"),
      cls: "bg-(--ld-positive-bg) text-(--ld-positive-ink)",
    },
    merged: {
      label: t("settings.branches.badgeMerged"),
      cls: "bg-(--ld-active) text-(--ld-accent)",
    },
    abandoned: {
      label: t("settings.branches.badgeAbandoned"),
      cls: "bg-(--ld-active) text-(--ld-text)",
    },
  }[status];
  return (
    <span className={`rounded px-1.5 py-0.5 font-medium text-xs ${config.cls}`}>
      {config.label}
    </span>
  );
}

function formatTimestamp(ts: string): string {
  const iso = ts.includes("T") ? ts : `${ts.replace(" ", "T")}Z`;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return ts;
  return new Date(ms).toLocaleString();
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
